import { type DepartureDto } from '@/types/webapi';

const REALTIME_THRESHOLD_MINUTES = 20 * 60 * 1000; // 20 minutes in milliseconds

/**
 * Sorts departure groups by time, using expectedStopTime if isCallingSoon is true
 * @param departureGroups - The departure groups to sort
 * @returns Sorted departure groups with departures ordered chronologically
 */
export function sortDepartureGroups(departureGroups: Record<string, DepartureDto[]>): Record<string, DepartureDto[]> {
    return Object.fromEntries(
        Object.entries(departureGroups).map(([key, departures]) => {
            const sorted = [...departures].sort((a, b) => {
                const aTime = a.isCallingSoon && a.expectedStopTime ? a.expectedStopTime : a.aimedStopTime;
                const bTime = b.isCallingSoon && b.expectedStopTime ? b.expectedStopTime : b.aimedStopTime;
                return new Date(aTime).getTime() - new Date(bTime).getTime();
            });
            return [key, sorted];
        })
    );
}

/**
 * Update the legs of the departures with time status flags
 * @param departureGroups - The departure groups to update
 * @param currentTime - The current time
 * @returns The updated departure groups
 */
export function updateDeparturesTimeStatusFlags(
    departureGroups: Record<string, DepartureDto[]>,
    currentTime: Date
): Record<string, DepartureDto[]> {
    return Object.keys(departureGroups).reduce(
        (updatedGroups, groupKey) => {
            updatedGroups[groupKey] = departureGroups[groupKey].map(departure => ({
                ...departure,
                estimatedCalls:
                    departure.estimatedCalls?.map(estimatedCall => {
                        if (!estimatedCall.aimedStopTime) return estimatedCall;

                        const stopTime = estimatedCall.aimedStopTime.getTime();
                        const currentTimeMs = currentTime.getTime();

                        return {
                            ...estimatedCall,
                            isCallingSoon: stopTime - currentTimeMs <= REALTIME_THRESHOLD_MINUTES
                        };
                    }) || []
            }));

            return updatedGroups;
        },
        {} as Record<string, DepartureDto[]>
    );
}

/**
 * Adjusts expectedStopTime to never be earlier than aimedStopTime
 * @param departureGroups - The departure groups to process
 * @returns Departure groups with corrected expectedStopTime values
 */
export function adjustExpectedTimesBasedOnAimed(departureGroups: Record<string, DepartureDto[]>): Record<string, DepartureDto[]> {
    return Object.keys(departureGroups).reduce(
        (adjustedGroups, groupKey) => {
            adjustedGroups[groupKey] = departureGroups[groupKey].map(departure => {
                const { expectedStopTime, aimedStopTime, estimatedCalls = [] } = departure;
                const expectedTime = expectedStopTime?.getTime();
                const aimedTime = aimedStopTime?.getTime();

                return {
                    ...departure,
                    expectedStopTime: expectedTime && expectedTime < aimedTime ? aimedStopTime : expectedStopTime,
                    // Adjust the expectedStopTime of each estimatedCall to never be earlier than the aimedStopTime
                    estimatedCalls: estimatedCalls?.map(estimatedCall => {
                        if (!estimatedCall.expectedStopTime || !estimatedCall.aimedStopTime) {
                            return estimatedCall;
                        }

                        const { expectedStopTime, aimedStopTime } = estimatedCall;
                        const expectedTime = expectedStopTime?.getTime();
                        const aimedTime = aimedStopTime?.getTime();

                        return {
                            ...estimatedCall,
                            expectedStopTime: expectedTime < aimedTime ? aimedStopTime : expectedStopTime
                        };
                    })
                };
            });
            return adjustedGroups;
        },
        {} as Record<string, DepartureDto[]>
    );
}

/**
 * Groups departures by quay
 * @param departureGroups - The departure groups to group by quay
 * @returns The departure groups grouped by quay
 */
export function groupByQuay(departureGroups: Record<string, DepartureDto[]>): Record<string, Record<string, DepartureDto[]>> {
    return Object.fromEntries(
        Object.entries(departureGroups).map(([key, departures]) => {
            // Group by quay id
            const quayGroups = departures.reduce(
                (acc, departure) => {
                    if (!departure.quay) return acc;
                    const quayKey = departure.quay.id;
                    if (!acc[quayKey]) {
                        acc[quayKey] = [];
                    }
                    acc[quayKey].push(departure);
                    return acc;
                },
                {} as Record<string, DepartureDto[]>
            );

            // Sort quay groups by quay publicCode
            const sortedQuayEntries = Object.entries(quayGroups).sort(([, a], [, b]) => {
                const aName = a[0]?.quay?.publicCode || '';
                const bName = b[0]?.quay?.publicCode || '';
                return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
            });

            return [key, Object.fromEntries(sortedQuayEntries)];
        })
    );
}

/**
 * Groups departures by line number
 * @param quayGroups - The quay groups to group by line number
 * @returns The quay groups grouped by line number
 */
export function groupByLine(
    quayGroups: Record<string, Record<string, DepartureDto[]>>
): Record<string, Record<string, Record<string, DepartureDto[]>>> {
    return Object.fromEntries(
        Object.entries(quayGroups).map(([quayKey, lineGroups]) => [
            quayKey,
            Object.fromEntries(
                Object.entries(lineGroups).map(([lineKey, departures]) => [
                    lineKey,
                    departures.reduce(
                        (acc, departure) => {
                            const lineId = departure.line?.id || '';
                            const tripHeadsign = departure.tripHeadsign;
                            const key = [lineId, tripHeadsign].filter(Boolean).join('|');
                            if (!acc[key]) {
                                acc[key] = [];
                            }
                            acc[key].push(departure);
                            return acc;
                        },
                        {} as Record<string, DepartureDto[]>
                    )
                ])
            )
        ])
    );
}
